import ReactDOM from 'react-dom/client';
import { EventType, InteractionRequiredAuthError, BrowserAuthError } from '@azure/msal-browser';
import type { AuthenticationResult } from '@azure/msal-browser';
import { msalInstance, loginRequest, apiTokenRequest } from '@/features/auth/authConfig';
import { setTokenAcquirer } from '@/lib/apiClient';
import { initCookieConsent } from '@/utils/thirdPartyScripts';
import '@/styles/index.css';
import { App } from '@/app/App';



// Initialize MSAL, authenticate, then render
void msalInstance.initialize().then(async () => {
  // Handle redirect response (returns null if not coming back from a redirect)
  const redirectResponse = await msalInstance.handleRedirectPromise();

  // If inside a hidden iframe (MSAL silent renewal), stop — don't render the app
  if (window !== window.parent) return;

  // Set active account from redirect response or existing accounts
  if (redirectResponse?.account) {
    msalInstance.setActiveAccount(redirectResponse.account);
  } else {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0]!);
    }
  }

  // If no account is signed in, trigger redirect login immediately
  if (!msalInstance.getActiveAccount()) {
    await msalInstance.loginRedirect(loginRequest);
    return; // Page will redirect — don't render
  }

  // Inject OneTrust cookie consent banner as early as possible — before any auth flow
  initCookieConsent();

  // Wire MSAL token acquisition into the fetch-based API client
  setTokenAcquirer(async () => {
    const account = msalInstance.getActiveAccount();
    if (!account) throw new Error('No active MSAL account');
    try {
      const response = await msalInstance.acquireTokenSilent({
        ...apiTokenRequest,
        account,
      });
      return response.accessToken;
    } catch (err) {
      if (
        err instanceof InteractionRequiredAuthError ||
        (err instanceof BrowserAuthError && err.errorCode === 'block_iframe_reload')
      ) {
        try {
          const response = await msalInstance.acquireTokenPopup({
            ...apiTokenRequest,
            account,
          });
          return response.accessToken;
        } catch {
          await msalInstance.acquireTokenRedirect({
            ...apiTokenRequest,
            account,
          });
          throw new Error('Redirecting for token acquisition');
        }
      }
      throw err;
    }
  });

  // Listen for sign-in events to keep active account in sync
  msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const result = event.payload as AuthenticationResult;
      if (result.account) {
        msalInstance.setActiveAccount(result.account);
      }
    }
  });

  ReactDOM.createRoot(document.getElementById('root')!).render(<App />);
});
